import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSiteConfig } from "../context/SiteConfigContext";
import { CategoryContext } from "../context/CategoryContext";

const Header = () => {
  const navigate = useNavigate();
  const { config } = useSiteConfig();
  const { categories } = useContext(CategoryContext);
  
  // Mobile states
  const [openMobDropdown, setOpenMobDropdown] = useState(null);
  const [openMobSub, setOpenMobSub] = useState(null);

  const toggleMobDropdown = (idx) => {
    setOpenMobDropdown(openMobDropdown === idx ? null : idx);
    setOpenMobSub(null); 
  };

  const toggleMobSub = (subIdx) => {
    setOpenMobSub(openMobSub === subIdx ? null : subIdx);
  };

  // Mobile navigation handler to fix redirection issue
  const handleMobNav = (path, hasDropdown, e) => {
    if (hasDropdown && path === "#!") {
      if (e) e.preventDefault();
      return; 
    }
    
    // Redirect using navigate
    navigate(path);
    
    // Bootstrap toggle manual close (optional but safe)
    const offcanvasElement = document.getElementById('ithMobileSidebar');
    if (offcanvasElement) {
        const bootstrapOffcanvas = window.bootstrap?.Offcanvas.getInstance(offcanvasElement);
        if (bootstrapOffcanvas) {
            bootstrapOffcanvas.hide();
        }
    }
  };

  if (!config) return null;

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about-us" },
    { name: "Products", path: "#!", hasDropdown: true },
    { name: "Services", path: "/services" },
    { name: "Expansion Zones", path: "/expansion-zones" },
    { name: "Contact Us", path: "/contact-us" },
    { name: "Career Path", path: "/career" },
  ];

  return (
    <header className="ith-main-header">
      {/* Top Bar */}
      <div className="ith-top-bar">
        <div className="container-fluid d-flex justify-content-between align-items-center h-100 px-lg-5">
          <div className="ith-contact-info d-flex gap-3">
             <Link to={`mailto:${config.email}`} className="small text-white text-decoration-none">
              <i className="fas fa-envelope me-1"></i> {config.email}
             </Link>
             <Link to={`tel:${config.phone}`} className="small text-white text-decoration-none">
              <i className="fas fa-phone-alt me-1"></i> {config.phone}
             </Link>
          </div>
          <div className="ith-top-marquee-container d-none d-md-block flex-grow-1">
            <marquee behavior="scroll" direction="left" scrollamount="5">
              {config.siteName || "ITH Group"} - The Future Of Hydraulics Begins Here
            </marquee>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav className="ith-navbar-container ">
        <div className="ith-logo-section">
          <Link to="/" className="text-decoration-none d-flex align-items-center">
            <img src={config.logo || "/images/logo.png"} alt="ITH Logo" className="ith-logo-img ith-rotate-logo-360" />
            <div className="ith-logo-text ms-2">
              <h1 className="ith-brand-name">{config.siteName || "ITH GROUP"}</h1>
              <p className="ith-brand-subtext">Industrial Trading House</p>
            </div>
          </Link>
        </div>

        {/* Desktop Menu */}
        <div className="ith-menu-section d-none d-lg-flex">
          <ul className="ith-nav-links list-unstyled d-flex m-0">
            {navLinks.map((link, index) => (
              <li key={index} className={link.hasDropdown ? "ith-has-dropdown" : ""}>
                <Link to={link.path}>{link.name}</Link>
                {link.hasDropdown && (
                  <ul className="ith-dropdown-menu-custom">
                    {categories.map((cat) => (
                      <li key={cat._id} className={cat.children?.length > 0 ? "ith-has-submenu" : ""}>
                        <Link to={`/categories/${cat.slugPath}`}>
                          {cat.name} 
                          {cat.children?.length > 0 && <i className="fas fa-chevron-right float-end mt-1"></i>}
                        </Link>
                        {cat.children?.length > 0 && (
                          <ul className="ith-submenu-custom">
                            {cat.children.map((sub, iIdx) => (
                              <li key={iIdx}>
                                <Link to={`/categories/${sub.slugPath}`}>{sub.name}</Link>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Hamburger */}
        <div className="ith-mobile-toggle d-lg-none">
          <button className="btn p-0 border-0 shadow-none" data-bs-toggle="offcanvas" data-bs-target="#ithMobileSidebar">
            <i className="fas fa-bars fa-2x hamburger"></i>
          </button>
        </div>
      </nav>

      {/* Mobile Sidebar */}
      <div className="offcanvas offcanvas-end ith-mobile-sidebar" tabIndex="-1" id="ithMobileSidebar">
        <div className="offcanvas-header border-bottom">
          <img src={config.logo || "/images/logo.png"} alt="Logo" style={{ height: '40px' }} />
          <button type="button" className="ith-btn-close-custom" data-bs-dismiss="offcanvas">
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="offcanvas-body p-0">
          <ul className="ith-sidebar-list list-unstyled">
            {navLinks.map((link, index) => (
              <li key={index} className="ith-sidebar-item">
                <div className="d-flex justify-content-between align-items-center w-100">
                  <span 
                    className="ith-mob-link flex-grow-1" 
                    onClick={(e) => handleMobNav(link.path, link.hasDropdown, e)}
                    style={{ cursor: 'pointer', padding: '10px 0', display: 'block' }}
                    data-bs-dismiss="offcanvas"
                  >
                    {link.name}
                  </span>
                  {link.hasDropdown && (
                    <span className="ith-mob-toggle-icon px-3" onClick={() => toggleMobDropdown(index)}>
                      {openMobDropdown === index ? '-' : '+'}
                    </span>
                  )}
                </div>

                {/* Mobile Dropdown (Categories) */}
                {link.hasDropdown && openMobDropdown === index && (
                  <ul className="ith-mob-sub-list list-unstyled ps-3">
                    {categories.map((cat, sIdx) => (
                      <li key={cat._id} className="py-2 border-bottom">
                        <div className="d-flex justify-content-between align-items-center pe-3">
                          <span 
                            className="ith-mob-sub-link flex-grow-1" 
                            onClick={() => handleMobNav(`/categories/${cat.slugPath}`, false)}
                            style={{ cursor: 'pointer' }}
                            data-bs-dismiss="offcanvas"
                          >
                            {cat.name}
                          </span>
                          {cat.children?.length > 0 && (
                            <span className="ith-mob-toggle-icon-sm px-2" onClick={() => toggleMobSub(sIdx)}>
                              {openMobSub === sIdx ? '-' : '+'}
                            </span>
                          )}
                        </div>
                        
                        {/* Sub-Sub Items */}
                        {openMobSub === sIdx && cat.children?.length > 0 && (
                          <ul className="list-unstyled ps-3 pt-2">
                            {cat.children.map((sub, iIdx) => (
                              <li key={iIdx} className="py-1">
                                <span 
                                  className="text-muted small d-block" 
                                  onClick={() => handleMobNav(`/categories/${sub.slugPath}`, false)}
                                  style={{ cursor: 'pointer' }}
                                  data-bs-dismiss="offcanvas"
                                >
                                  {sub.name}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </header>
  );
};

export default Header;